import { useProtectedContent } from "../hooks/useProtectedContent";

import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutlined";
import PeopleIcon from "@mui/icons-material/People";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckroomIcon from "@mui/icons-material/Checkroom";
import ListIcon from "@mui/icons-material/List";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import UndoIcon from "@mui/icons-material/Undo";
import SubjectIcon from "@mui/icons-material/Subject";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DescriptionIcon from "@mui/icons-material/Description";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SupportIcon from "@mui/icons-material/Support";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ChecklistIcon from "@mui/icons-material/Checklist";
import { useBulkOrderMode } from "../hooks/useAuthorisation";
import { getDraftBasePath } from "./orderPaths";

export function useDashboardTiles(dashInfo) {
  const bulkOrderMode = useBulkOrderMode();
  const startOrderLink = getDraftBasePath(bulkOrderMode);

  const tiles = {
    staffTiles: [
      {
        heading: "Add Staff",
        link: "/staff/add",
        icon: <PersonAddIcon />,
        iconColour: "error.light",
        enabled: useProtectedContent("staffCreate"),
      },
      {
        heading: "All Staff",
        subheading: dashInfo?.staffCount,
        link: "/staff",
        icon: <GroupsIcon />,
        iconColour: "error.light",
        enabled: useProtectedContent("allowstaff"),
      },
      {
        heading: "Active Staff",
        subheading: dashInfo?.activeStaffCount,
        link: "/staff/active",
        icon: <PeopleIcon />,
        iconColour: "error.light",
        enabled: useProtectedContent("allowstaff"),
      },
      {
        heading: "Inactive Staff",
        subheading: dashInfo?.inactiveStaffCount,
        link: "/staff/inactive",
        icon: <PeopleOutlineIcon />,
        iconColour: "error.light",
        enabled: useProtectedContent("allowstaff"),
      },
    ],
    orderTiles: [
      {
        heading: "Start Order",
        link: startOrderLink,
        icon: <AddShoppingCartIcon />,
        iconColour: "success.main",
        enabled: useProtectedContent("alloworders"),
      },
      {
        heading: "Draft Orders",
        subheading: dashInfo?.draftOrdersCount,
        link: "/draft-orders",
        icon: <ShoppingCartRoundedIcon />,
        iconColour: "success.main",
        enabled: useProtectedContent("alloworders"),
      },
      {
        heading: "Received Orders",
        subheading: dashInfo?.orders,
        link: "/received-orders",
        icon: <CheckBoxOutlineBlankIcon />,
        iconColour: "success.main",
        enabled: useProtectedContent("alloworders"),
      },
      {
        heading: "Completed Orders",
        subheading: dashInfo?.invoiceCount,
        link: "/completed-orders",
        icon: <CheckBoxIcon />,
        iconColour: "success.main",
        enabled: useProtectedContent("alloworders"),
      },
    ],
    staffRequestTiles: [
      {
        heading: "Start Request",
        link: "/request",
        icon: <AddShoppingCartIcon />,
        iconColour: "success.main",
        enabled: useProtectedContent("staffRequestCreate"),
      },
      {
        heading: "Draft Requests",
        subheading: `${
          Number(dashInfo?.draftOrdersCount) -
          Number(dashInfo?.myRequestsAwaitingApproval)
        }`, // Change to count of draft requests
        link: "/draft-requests",
        icon: <ShoppingCartRoundedIcon />,
        iconColour: "success.main",
        enabled: useProtectedContent("staffRequestCreate"),
      },
      {
        heading: "Submitted Requests",
        subheading: dashInfo?.myRequestsAwaitingApproval, // Count of requests submitted for approval
        link: "/submitted-requests",
        icon: <CheckBoxOutlineBlankIcon />,
        iconColour: "success.main",
        enabled: useProtectedContent("staffRequestCreate"),
      },
      {
        heading: "Request History",
        // subheading: dashInfo?.invoiceCount, // Change to only show orders created by this user
        link: "/request-history",
        icon: <CheckBoxIcon />,
        iconColour: "success.main",
        enabled: useProtectedContent("staffRequestCreate"),
      },
    ],
    staffRequestApprovalTiles: [
      {
        heading: "Pending Approval",
        subheading: dashInfo?.requestsAwaitingApproval, // Count of requests pending user approval
        link: "/approvals",
        icon: <ChecklistIcon />,
        iconColour: "success.main",
        enabled: useProtectedContent("staffRequestApprove"),
      },
      {
        heading: "Approved Requests",
        subheading: dashInfo?.myApprovedOrdersCount,
        link: "/approved-orders",
        icon: <CheckBoxOutlineBlankIcon />,
        iconColour: "success.main",
        enabled: useProtectedContent("staffRequestApprove"),
      },
      {
        heading: "Completed Requests",
        subheading: dashInfo?.myApprovedInvoiceCount,
        link: "/approved-invoices",
        icon: <CheckBoxIcon />,
        iconColour: "success.main",
        enabled: useProtectedContent("staffRequestApprove"),
      },
    ],
    wardrobeTiles: [
      {
        heading: "Wardrobe Request",
        link: "/wardrobe-request",
        icon: <PlaylistAddIcon />,
        enabled: useProtectedContent("allowwardrobe"),
      },
      {
        heading: "Wardrobe List",
        subheading: dashInfo?.wardrobeCount,
        link: "/wardrobes",
        icon: <CheckroomIcon />,
        enabled: useProtectedContent("allowwardrobe"),
      },
      {
        heading: "Item List",
        subheading: dashInfo?.wardrobeAllItemsCount,
        link: "/items",
        icon: <ListIcon />,
        enabled: useProtectedContent("allowwardrobe"),
      },
      {
        heading: "Inspiration",
        link: "https://shop.peterdrew.com/",
        icon: <LightbulbIcon />,
        enabled: useProtectedContent("allowwardrobe"),
        newTab: true,
      },
    ],
    adminTiles: [
      {
        heading: "My Account",
        link: "/account",
        icon: <ManageAccountsIcon />,
        enabled: true,
      },
      // {
      //   heading: "Wardrobe Request",
      //   link: "/wardrobe-request",
      //   icon: <PlaylistAddIcon />,
      //   checkAuth: "allowwardrobe",
      //   enabled: useProtectedContent("allowwardrobe"),
      // },
      {
        heading: "Contracts",
        subheading: dashInfo?.contractsCount,
        link: "/contracts",
        icon: <NoteAltIcon />,
        checkAuth: "allowcontracts",
        enabled: useProtectedContent("allowcontracts"),
      },
      {
        heading: "Support",
        link: "/support",
        icon: <SupportIcon />,
        enabled: true,
      },
    ],
    returnTiles: [
      {
        heading: "Start Return",
        link: "/returns/new",
        icon: <UndoIcon />,
        iconColour: "warning.main",
        enabled: useProtectedContent("allowreturns"),
      },
      {
        heading: "Previous Orders",
        subheading: dashInfo?.invoiceCount,
        link: "/completed-orders",
        icon: <SubjectIcon />,
        iconColour: "warning.main",
        enabled: useProtectedContent("allowreturns"),
      },
      {
        heading: "Pending Returns",
        subheading: dashInfo.receivedReturnOrderCount,
        link: "/returns/pending",
        icon: <CheckBoxOutlineBlankIcon />,
        iconColour: "warning.main",
        enabled: useProtectedContent("allowreturns"),
      },
      {
        heading: "Completed Returns",
        subheading: dashInfo.completedReturnOrderCount,
        link: "/returns/completed",
        icon: <CheckBoxIcon />,
        iconColour: "warning.main",
        enabled: useProtectedContent("allowreturns"),
      },
    ],
    entitlementTiles: [
      {
        heading: "Entitlement Remaining",
        subheading: dashInfo.entitlementRemainingCount,
        link: "/entitlement/remaining",
        icon: <RefreshIcon />,
        iconColour: "entitlement.main",
        enabled: useProtectedContent("allowentitlement"),
      },
      {
        heading: "Entitlement Complete",
        subheading: dashInfo.entitlementCompletedCount,
        link: "/entitlement/complete",
        icon: <PublishedWithChangesIcon />,
        iconColour: "entitlement.main",
        enabled: useProtectedContent("allowentitlement"),
      },
      {
        heading: "Entitlement Exceeded",
        subheading: dashInfo.entitlementExceededCount,
        link: "/entitlement/exceeded",
        icon: <SyncProblemIcon />,
        iconColour: "entitlement.main",
        enabled: useProtectedContent("allowentitlement"),
      },
      {
        heading: "Predicted Entitlement",
        subheading: dashInfo.entitlementPredictedCount,
        link: "/entitlement/predicted",
        icon: <EventRepeatIcon />,
        iconColour: "entitlement.main",
        enabled: useProtectedContent("allowentitlement"),
      },
    ],
    financeTiles: [
      {
        currency: true,
        heading: "Account Balance",
        subheading: dashInfo.balanceDue,
        link: "/balance",
        icon: <AccountBalanceWalletIcon />,
        iconColour: "info.main",
        enabled: useProtectedContent("allowfinances"),
      },
      {
        heading: "Outstanding Invoices",
        subheading: dashInfo.dueInvoiceCount,
        link: "/invoices/outstanding",
        icon: <DescriptionOutlinedIcon />,
        iconColour: "info.main",
        enabled: useProtectedContent("allowfinances"),
      },
      {
        heading: "Settled Invoices",
        subheading: dashInfo.paidInvoiceCount,
        link: "/invoices/settled",
        icon: <DescriptionIcon />,
        iconColour: "info.main",
        enabled: useProtectedContent("allowfinances"),
      },
      {
        heading: "Outstanding Credit",
        subheading: dashInfo.crMemoCount,
        link: "/credit/outstanding",
        icon: <DescriptionOutlinedIcon />,
        iconColour: "info.main",
        enabled: useProtectedContent("allowfinances"),
      },
    ],
  };

  return tiles;
}
